import React from 'react';
import { MegaMenu } from './MegaMenu';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { Link } from 'react-router-dom';


const AICertificationPage = () => {
  // Generate array of 8 university logos
  const universities = Array.from({ length: 8 }, (_, index) => ({
    name: `University ${index + 1}`,
    logo: `/universities/${index + 1}.png`
  }));

  const schedule = [
    {
      day: 'Day 1',
      title: 'Meet Your New Best Friend: AI!',
      activities: [
        'Basics of AI, Machine Learning, and Generative AI',
        'Introduction to Large Language Models (LLMs) like GPT-4o',
        'Safe and effective prompting techniques',
      ],
    },
    {
      day: 'Day 2',
      title: 'Creative Writing & Learning using AI & Increase Confidence & Presentation Skills',
      activities: [
        'AI-generated comics and visual storytelling',
        'Prompt-based creativity using images and scripts',
        'Making engaging presentations and AI-generated documents',
        'Building confidence through structured speaking',
      ],
    },
    {
      day: 'Day 3',
      title: 'Turn Your Imagination into Art with AI & Voice generation and Avatar creation with AI',
      activities: [
        'Creating cinematic visual effects and realistic images',
        'Poster design and character illustration',
        'Voice generation and sound design with AI',
        'Creating AI video avatars and syncing with voice',
      ],
    },
    {
      day: 'Day 4',
      title: 'Build No-code website & games with AI & Meet Your Own Personal AI Agent',
      activities: [
        'No-code web development for school websites or personal projects',
        'Project planning and structure using AI',
        'Discover how AI agents can help in studies, ideas, and daily tasks',
        'Create your own smart assistant using simple AI tools',
      ],
    },
    {
      day: 'Day 5',
      title: 'Final Project & Graduation',
      activities: [
        'Final review and student presentations',
        'Discussion of main project and certifications',
      ],
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <MegaMenu />

      {/* Hero Section */}
      <div className="relative h-[100vh]">
        {/* Background Image */}
        <div className="absolute inset-0 h-full">
          <img
            src="/images/aibg2.jpg"
            alt="AI Course Background"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Overlay Content */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 md:px-8 lg:px-16 bg-black bg-opacity-60 space-y-4">
          <h1 className="text-white text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight lg:leading-snug noto">
            Artificial Intelligence – Online Program
          </h1>
          <p className="text-white text-xl md:text-2xl">(15 hours)</p>
          <p className="text-white hidden sm:block max-w-4xl">
            An accessible yet rigorous introduction to the world of Artificial Intelligence. <br />
            Explore philosophy, cognitive science, history, ethics, and governance through a transformative learning experience.
          </p>
          <Link to="https://forms.gle/sDZCD9CR3HfKGgnPA" target="_blank" className="w-full sm:w-auto">
              <button className="bg-blue-400 text-white px-4 mt-5 sm:px-6 py-[10px] rounded-full font-medium shadow-lg hover:bg-blue-400 hover:text-white transition-all sm:w-60 md:w-72 lg:w-[330px] poppins text-xs sm:text-sm md:text-base">
                Enroll Now
              </button>
            </Link>
        </div>
      </div>

      {/* Seminar Overview Section */}
      <section className="container mx-auto px-4 py-10 w-full">
        <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-6 pt-6 text-gray-800">
          Seminar Overview
        </h2>

        {/* Brief intro */}
        <p className="text-gray-600 leading-relaxed text-base lg:text-lg text-center px-4 sm:px-8 lg:px-16 xl:px-24 max-w-4xl mx-auto mb-10">
          This seminar offers students an accessible yet rigorous introduction to the world of Artificial Intelligence.
          It is not a technical coding course but an exploration of philosophy, cognitive science, history, ethics, and governance
          over five days of transformative learning.
        </p>

        {/* Key aspects in cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 container mx-auto">
          <div className="bg-blue-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-4xl mb-4">🤔</div>
            <h3 className="font-bold text-lg mb-2 text-gray-800">Critical Thinking</h3>
            <p className="text-gray-600 text-sm">
              Explore fundamental questions about machine intelligence, ethics, and accountability in the age of AI
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-4xl mb-4">📚</div>
            <h3 className="font-bold text-lg mb-2 text-gray-800">Historical Context</h3>
            <p className="text-gray-600 text-sm">
              Trace AI's development from 1800s philosophy to modern Generative AI breakthroughs and societal impacts
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow duration-300">
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="font-bold text-lg mb-2 text-gray-800">Future Applications</h3>
            <p className="text-gray-600 text-sm">
              Discover AI's possibilities in education, science, and creativity while understanding its risks and limits
            </p>
          </div>
        </div>
      </section>

      {/* Visual Feature Section */}
      <section className="py-8">
        <div className="mx-auto">
          <img
            src="/images/ai-feature-6.jpg"
            alt="AI Learning Experience"
            className="w-full mx-auto h-[250px] md:h-[350px] object-cover"
          />
        </div>
      </section>

      {/* Schedule and Topics Section */}
      <section className="bg-gray-50 py-10">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-10 text-gray-800">
            Schedule and Topics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedule.map((day, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="bg-blue-500 text-white px-4 py-2 rounded-md inline-block mb-4 font-semibold">
                  {day.day}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 min-h-[60px]">
                  {day.title}
                </h3>
                <ul className="space-y-2">
                  {day.activities.map((activity, actIndex) => (
                    <li key={actIndex} className="text-gray-600 text-sm flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span>{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Philosophy Section */}
      <section className="container mx-auto px-4 py-10">
        <div className="text-center px-4 sm:px-8 lg:px-16 xl:px-24 2xl:px-32">
          <p className="text-gray-600 leading-relaxed text-sm sm:text-base lg:text-lg">
            Throughout the seminar, students will be encouraged to critically inquire and reflect, engaging in discourse as a means of learning both collectively and individually. In doing so, they will practice critical thinking skills and, even without prior knowledge of AI, begin to see how this technology might be applied in ways that maximize its value for both society and them.
          </p>
        </div>
      </section>

      {/* Assessment & Certification Section */}
      <section className="bg-blue-900 text-white py-10">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-8">
            Assessment & Certification
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-blue-800 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Assessment</h3>
              <p className="text-blue-100">
                Primarily reflective and participatory (e.g., contributions in discussions, group outputs). No formal grading.
              </p>
            </div>
            <div className="bg-blue-800 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Certification</h3>
              <p className="text-blue-100">
                Letter of Completion awarded to all participants who attend and participate (issued by Dean's Office)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Universities Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-10 text-gray-800">
          Our Partner Universities
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {universities.map((university, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center hover:shadow-xl transition-shadow duration-300"
            >
              <img
                src={university.logo}
                alt={university.name}
                className="w-full h-auto aspect-square object-contain"
              />
            </div>
          ))}
        </div>
        <p className="text-center text-gray-600 mt-8 text-sm">and many more...</p>
      </section>

      {/* Get Started Section */}
      <section className="py-10 px-3 lg:px-0">
        <div className="max-w-screen-lg mx-auto bg-blue-700 text-center text-white p-8 sm:p-12 rounded-lg">
          <h2 className="text-2xl lg:text-4xl font-bold mb-4">
            Get Started Today
          </h2>
          <p className="text-base lg:text-xl leading-relaxed max-w-3xl mx-auto">
            Ready to embark on your AI learning journey? Contact us to learn more about enrollment and upcoming sessions for this transformative 15-hour online program.
          </p>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default AICertificationPage;

